/* eslint-disable @typescript-eslint/no-unused-vars */
import { useEffect, useRef, useState } from "react";
import {
  IconMenu2,
  IconX,
  IconBell,
  IconSearch,
  IconUser,
  IconSettings,
  IconLogout,
  IconChevronRight,
  IconPencil,
} from "@tabler/icons-react";
import { fetch3 } from "../commonApis/commonApis";
import {
  useProfileData,
  useUpdateProfilePicMutation,
} from "../apis/profileApis";
import { useQueryClient } from "@tanstack/react-query";
import { useFetchNotificationsData } from "../apis/notificationApis";
import { useMerchantAllowedShippingChannels } from "../apis/integrationApis";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";
type Props = {
  notification: number;
};

export default function Header({ notification }: Props) {
  const [activeItem, setActiveItem] = useState("Dashboard");
  const [showSearch, setShowSearch] = useState(false);
  const [showSidebar, setShowSidebar] = useState(false);
  const [showNotification, setShowNotification] = useState(false);
  const [showProfileDropdown, setShowProfileDropdown] = useState(false);
  const profileRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();
  const [searchFor, setSearchFor] = useState<string>("");
  const [pageNo, setPageNo] = useState<number>(1);
  const notificationRef = useRef<HTMLDivElement>(null);
  const profile = useSelector((state: any) => state.auth.profile);

  const url = process.env.REACT_APP_BASE_URL;
  const queryClient = useQueryClient(); // initialize react-query client

  const { data: UserData, refetch } = useProfileData();
  const clientId = UserData?.id || UserData?.clientId || "";
  const { data: allowedChannels } = useMerchantAllowedShippingChannels(clientId);

  const hasIntegrationAccess = Array.isArray(allowedChannels) && allowedChannels.length > 0;

  const navItems = [
    { name: "Dashboard", path: "/dashboard" },
    { name: "Order", path: "/orders" },
    { name: "Manifest", path: "/manifest" },
    { name: "Inventory", path: "/inventory-mangement" },
    // { name: "Dark Store", path: "/dark-store" },
    { name: "Invoice", path: "/invoice-management" },
    ...(hasIntegrationAccess ? [{ name: "Integration", path: "/integration" }] : []),
    { name: "Help & Support", path: "/help-and-support" },
  ];
  const location = useLocation();

  useEffect(() => {
    setActiveItem(location.pathname);
  }, [location.pathname]);

  // const handleNavClick = (path: string) => {
  //   setActiveItem(path);
  //   setShowSidebar(false);
  // };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        profileRef.current &&
        !profileRef.current.contains(event.target as Node)
      ) {
        setShowProfileDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    const handleClickOutsideNoti = (event: MouseEvent) => {
      if (
        notificationRef.current &&
        !notificationRef.current.contains(event.target as Node)
      ) {
        setShowNotification(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutsideNoti);
    return () =>
      document.removeEventListener("mousedown", handleClickOutsideNoti);
  }, []);

  const {
    data,
    isPending: listPending,
    refetch: fetchNotifications,
  } = useFetchNotificationsData(pageNo, searchFor);

  const [avatar, setAvatar] = useState("/img/dumy-profile.png");
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const { mutate } = useUpdateProfilePicMutation();

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const previewUrl = URL.createObjectURL(file);
      setAvatar(previewUrl);
    }
    mutate(
      {
        companyLogo: file,
      },
      {
        onSuccess: (data) => {
          refetch();
        },
        onError: (error) => {
          toast.error(`${error}`);
        },
      },
    );
  };

  const handleLogout = async () => {
    await fetch3(`${url}/api/auth/logout?type=lmClient`, "get");

    // clear token
    localStorage.removeItem("token");
    sessionStorage.removeItem("token");

    // clear profile query cache so app knows user is logged out
    queryClient.removeQueries({ queryKey: ["getMe"] });

    navigate("/", { replace: true });
  };

  useEffect(() => {
    if (notification !== undefined) {
      fetchNotifications();
    }
  }, [notification, fetchNotifications]);

  // helper function
  const timeAgo = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now.getTime() - date.getTime()) / 1000); // in seconds

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    if (diff < 2592000) return `${Math.floor(diff / 86400)}d ago`;
    return date.toLocaleDateString(); // fallback for old notifications
  };

  const handleNavClick = (path: string, e?: React.MouseEvent) => {
    if (e && (e.ctrlKey || e.metaKey)) {
      // Ctrl+Click (Windows/Linux) or ⌘+Click (Mac)
      window.open(path, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(path);
    setActiveItem(path);
    setShowSidebar(false);
  };

  const LogoComponent =
    process.env.REACT_APP_EXTERNAL_ADMIN === "true"
      ? "/logo/logo-black.png"
      : "/img/dashboard-icons/logo-tracktech.svg";

  return (
    <div className="p-4 sticky top-0 z-50">
      <header className="w-full flex items-center justify-between px-6 py-2 border border-white  bg-[#F7F7F5]/50 rounded-full shadow backdrop-blur-md">
        <div
          onClick={() => navigate("/dashboard")}
          className="flex items-center gap-2 cursor-pointer"
        >
          <img src={LogoComponent} alt="Logo" className="max-w-[160px]" />
          <div className="px-4 mt-3 py-1 text-xs text-black font-medium border border-[#212121] rounded-full uppercase">
            Client
          </div>
        </div>
        <nav
          className={`hidden xl:flex items-center gap-2 transition-all duration-300 bg-[#F7F7F5] rounded-lg ease-in-out ${
            showSearch ? "w-0 h-12 overflow-hidden" : "w-auto p-1"
          }`}
        >
          {navItems?.map((item) => {
            if (!item.name) return null;
            const isActive =
              activeItem === item.path ||
              (item.path !== "/" && location.pathname.startsWith(item.path));

            return (
              <a
                key={item.path}
                href={item.path}
                onClick={(e) => {
                  e.preventDefault();
                  handleNavClick(item.path, e);
                }}
                className={`relative group overflow-hidden px-4 py-2 text-sm rounded-lg whitespace-nowrap transition-all duration-300 ease-out select-none transform active:scale-[0.96] ${
                  isActive
                    ? "bg-[#212121] text-white font-bold shadow-sm"
                    : "bg-transparent text-[#212121]/70 font-semibold hover:text-[#212121]"
                }`}
              >
                {!isActive && (
                  <i className="absolute left-0 top-[120%] group-hover:-top-[40%] w-full h-[180%] bg-[#000000] rounded-lg opacity-0 group-hover:opacity-100 transition-all duration-500 ease-out pointer-events-none overflow-hidden z-10">
                    <span className="absolute top-1/2 left-1/2 w-[200%] h-[200%] bg-[radial-gradient(circle,_rgba(255,255,255,0.25)_0%,_rgba(255,255,255,0.05)_45%,_transparent_70%)] gooey-bg pointer-events-none" />
                  </i>
                )}
                <span
                  className={`relative z-20 transition-colors duration-500 inline-block ${
                    !isActive ? "group-hover:text-white" : ""
                  }`}
                >
                  {item.name}
                </span>
              </a>
            );
          })}
        </nav>

        <div className="flex items-center space-x-3 relative">
          <div
            className={`relative flex items-center transition-all duration-300 ease-in-out overflow-hidden border border-[#FFFFFF]/20 cursor-pointer rounded-full ${
              showSearch ? "w-96 bg-[#F7F7F5]" : "w-12 bg-transparent"
            } h-12 shadow-sm`}
          >
            <div
              className="w-12 h-12 flex justify-center items-center text-[#212121] bg-[#F7F7F5]"
              onClick={() => !showSearch && setShowSearch(true)}
            >
              <IconSearch strokeWidth={1.5} size={24} />
            </div>

            {showSearch && (
              <>
                <input
                  type="text"
                  placeholder="Search AWB, Order, User..."
                  autoFocus
                  onClick={(e) => e.stopPropagation()}
                  className="flex-1 h-full text-sm text-[#212121]  placeholder-[#212121]/70 border-none outline-none bg-transparent pr-2"
                />
                <div
                  className="w-12 h-12 flex justify-center items-center text-[#212121] transition"
                  onClick={() => setShowSearch(false)}
                >
                  <IconX className="w-5 h-5" />
                </div>
              </>
            )}
          </div>

          <div ref={notificationRef}>
            <div
              className="w-12 h-12 flex justify-center items-center rounded-full border border-[#FFFFFF]/20 cursor-pointer relative bg-[#F7F7F5]"
              onClick={() => setShowNotification((prev) => !prev)}
            >
              <IconBell
                strokeWidth={1.5}
                size={24}
                className="text-[#212121]"
              />
              {/* )} */}
            </div>

            {notification > 0 && (
              <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                {notification}
              </span>
            )}

            {showNotification && (
              <div className="absolute right-0 top-[66px] w-96 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg z-[1000] p-4">
                <h3 className="text-lg mb-2">Notifications</h3>

                <div className="-mx-4 max-h-96 overflow-y-auto">
                  {data?.notifications?.length > 0 ? (
                    data?.notifications?.slice(0, 5)?.map((item: any) => (
                      <div
                        key={item.id}
                        className="relative p-4 pr-6 border-b hover:bg-black/5 cursor-pointer transition"
                        onClick={() => navigate("/notifications")}
                      >
                        {item.image ? (
                          <div className="flex items-start gap-2">
                            <img
                              src={
                                item.image.startsWith("http")
                                  ? item.image
                                  : `${process.env.REACT_APP_IMAGE_BASE_URL}/${item.image}`
                              }
                              alt={item.title}
                              className="size-10 rounded-full object-cover"
                            />
                            <div>
                              <h3 className="text-sm font-medium mb-1">
                                {item.title}
                              </h3>
                              <p className="text-[#212121]/70 text-sm">
                                {item.message}
                              </p>
                              <p className="text-xs text-gray-500 mt-1">
                                {timeAgo(item.created_at)}
                              </p>
                            </div>
                          </div>
                        ) : (
                          <>
                            <h3 className="text-sm font-medium mb-1">
                              {item.title}
                            </h3>
                            <p className="text-[#212121]/70 text-sm">
                              {item.message}
                            </p>
                            <p className="text-xs text-gray-500 mt-1">
                              {timeAgo(item.created_at)}
                            </p>
                          </>
                        )}

                        {/* Red dot if not read / new */}
                        {!item.seen && (
                          <div className="size-2 bg-red-500 rounded-full absolute right-4 top-6"></div>
                        )}
                      </div>
                    ))
                  ) : (
                    <p className="text-center text-sm text-gray-500 py-6">
                      No notifications
                    </p>
                  )}
                </div>

                {data?.notifications?.length > 5 && (
                  <div className="border-t border-[#FFFFFF]/20 p-2 text-center">
                    <button
                      onClick={() => {
                        navigate("/notifications");
                      }}
                      className="text-sm text-[#212121]/70 font-medium hover:text-[#FBD037] hover:underline"
                    >
                      Show More
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="relative" ref={profileRef}>
            <div
              className="w-12 h-12 flex justify-center items-center rounded-full overflow-hidden cursor-pointer"
              onClick={() => setShowProfileDropdown((prev) => !prev)}
            >
              <img
                className="w-12 h-12 object-cover rounded-full"
                src={
                  UserData?.companyLogo
                    ? UserData?.companyLogo
                    : "/img/dumy-profile.png"
                }
                alt="User Avatar"
              />
            </div>
            {showProfileDropdown && (
              <div className="absolute right-0 top-[66px] w-72 bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] border border-[#FFFFFF]/10 rounded-xl shadow-lg z-[1000]">
                <div className="flex items-center gap-2 px-4 py-4 border-b border-[#E5E7EB]">
                  <div className="relative group w-14 h-14 rounded-full overflow-hidden flex-shrink-0">
                    <img
                      className="w-14 h-14  rounded-full object-cover"
                      src={
                        UserData?.companyLogo
                          ? UserData?.companyLogo
                          : "/img/dumy-profile.png"
                      }
                      alt="User Avatar"
                    />
                    {/* Hover Pencil Overlay */}
                    <div
                      onClick={() => fileInputRef.current?.click()}
                      className="absolute inset-0 bg-black/50 flex items-center justify-center opacity-0 group-hover:opacity-100 cursor-pointer transition"
                    >
                      <IconPencil size={24} color="white" />
                    </div>
                    {/* Hidden File Input */}
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      className="hidden"
                      onChange={handleFileChange}
                    />
                  </div>
                  {/* <img
                    className="w-14 h-14 object-cover rounded-full"
                    src={
                      UserData?.companyLogo
                        ? UserData?.companyLogo
                        : "/img/dumy-profile.png"
                    }
                    alt="User Avatar"
                  /> */}
                  <div>
                    <h2 className="text-[#212121] text-sm font-medium">
                      {UserData?.name ?? UserData?.storeName}
                    </h2>
                    <p className="text-[#212121]/70 text-xs">
                      {UserData?.email}
                    </p>
                  </div>
                </div>
                <ul className="py-2 space-y-2 text-sm text-[#212121]">
                  <li
                    className="px-4 py-2 hover:bg-[#F7F6F2] cursor-pointer flex justify-between items-center"
                    onClick={() => {
                      navigate("/profile");
                      setShowProfileDropdown(false);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      <IconUser /> My Profile
                    </div>
                    <IconChevronRight color="#6B7280" />
                  </li>
                  <li
                    className="px-4 py-2 hover:bg-[#F7F6F2] cursor-pointer flex justify-between items-center"
                    onClick={() => {
                      navigate("/settings");
                      setShowProfileDropdown(false);
                    }}
                  >
                    <div className="flex items-center gap-1">
                      <IconSettings /> Settings
                    </div>
                    <IconChevronRight color="#6B7280" />
                  </li>
                  <li
                    className="px-4 py-2 hover:bg-[#F7F6F2] cursor-pointer flex items-center gap-1"
                    onClick={() => {
                      handleLogout();
                      setShowProfileDropdown(false);
                    }}
                  >
                    <IconLogout /> Logout
                  </li>
                </ul>
              </div>
            )}
          </div>

          <div
            onClick={() => setShowSidebar(!showSidebar)}
            className="w-12 h-12 xl:hidden flex justify-center items-center rounded-full border border-[#FFFFFF]/20 cursor-pointer"
          >
            <IconMenu2 className="w-6 h-6 text-[#D2D2D2]" />
          </div>
        </div>
      </header>

      {showSidebar && (
        <>
          <div
            className="fixed inset-0 bg-black/30 z-40"
            onClick={() => setShowSidebar(false)}
          ></div>

          <aside className="fixed top-0 right-0 w-64 h-full bg-[#1f1f1f] z-50 p-4 shadow-2xl border-l border-[#FFFFFF]/10 xl:hidden transition-transform duration-300">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-white text-lg font-semibold">Menu</h2>
              <IconX
                onClick={() => setShowSidebar(false)}
                className="w-6 h-6 text-white cursor-pointer"
              />
            </div>
            <nav className="flex flex-col space-y-2 h-full pb-10 overflow-hidden overflow-y-auto">
              {navItems.map((item) => (
                <div
                  key={item.path}
                  onClick={() => handleNavClick(item.path)}
                  className={`px-4 py-3 text-sm rounded cursor-pointer ${
                    activeItem === item.path
                      ? "bg-[#FBD037] text-black"
                      : "text-white hover:bg-white/10"
                  }`}
                >
                  {item.name}
                </div>
              ))}
            </nav>
          </aside>
        </>
      )}
    </div>
  );
}
